import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import {
  getAllBusinessPointSettings,
  updateBusinessPointSetting,
  createBusinessPointSetting
} from '../../../../redux/handlers/market.handler';
import { ProductSuccessModal } from './ProductSuccessModal';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';

const EXPIRATION_TYPES = {
  NONE: 1,
  GRADUAL: 2,
  FIXED_DATE: 3
};

const EXPIRE_FROM_TYPES = {
  EARNED_DATE: 'EARNED_DATE',
  EARNED_YEAR: 'EARNED_YEAR'
};

const DEFAULT_FORM_DATA = {
  campaignFormat: 'คะแนนแลกส่วนลด',
  usagePoint: 0,
  discount: 0,
  point_expire: EXPIRATION_TYPES.NONE,
  expire_duration: 12,
  expire_from: EXPIRE_FROM_TYPES.EARNED_DATE,
  expire_years: 1,
  expire_day_month: '01/01'
};

export default function SettingUsagePoint() {
  const { businessSlug } = useParams();
  const dispatch = useDispatch();

  const [formdata, setFormdata] = useState(DEFAULT_FORM_DATA);
  const [existingSettingId, setExistingSettingId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formdisabled, setFormdisabled] = useState(false);

  const schema = yup.object().shape({
    usagePoint: yup.number().required('กรุณาระบุคะแนนที่ใช้').min(1, 'คะแนนที่ใช้ต้องมากกว่า 0'),
    discount: yup.number().required('กรุณาระบุส่วนลดที่ได้รับ').min(1, 'ส่วนลดที่ได้รับต้องมากกว่า 0'),
    expire_day_month: yup.string().when('point_expire', {
      is: EXPIRATION_TYPES.FIXED_DATE,
      then: schema => schema.required('กรุณาระบุวัน/เดือน').matches(/^\d{2}\/\d{2}$/, 'รูปแบบต้องเป็น วว/ดด'),
      otherwise: schema => schema
    }),
    expire_duration: yup.number().when('point_expire', {
      is: EXPIRATION_TYPES.GRADUAL,
      then: schema => schema.required('กรุณาระบุระยะเวลา').min(1, 'ระยะเวลาต้องมากกว่า 0'),
      otherwise: schema => schema
    }),
    expire_years: yup.number().when('point_expire', {
      is: EXPIRATION_TYPES.FIXED_DATE,
      then: schema => schema.required('กรุณาระบุจำนวนปี').min(1, 'จำนวนปีต้องมากกว่า 0'),
      otherwise: schema => schema
    })
  });

  const formatDateThai = date => {
    return date.toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const generateUniqueCode = () => {
    return Date.now().toString().slice(-6);
  };

  const mapExpirationToForm = expiration => {
    if (!expiration) return { point_expire: EXPIRATION_TYPES.NONE };

    switch (expiration.expiration_type) {
      case 'NONE':
        return { point_expire: EXPIRATION_TYPES.NONE };

      case 'GRADUAL':
        return {
          point_expire: EXPIRATION_TYPES.GRADUAL,
          expire_duration: expiration.expire_amount || 12,
          expire_from: expiration.expire_from || EXPIRE_FROM_TYPES.EARNED_DATE
        };

      case 'FIXED_DATE':
        const expireDate = new Date(expiration.expire_fixed_date);
        const day = String(expireDate.getDate()).padStart(2, '0');
        const month = String(expireDate.getMonth() + 1).padStart(2, '0');
        const currentYear = new Date().getFullYear();
        const years = expireDate.getFullYear() - currentYear;

        return {
          point_expire: EXPIRATION_TYPES.FIXED_DATE,
          expire_day_month: `${day}/${month}`,
          expire_years: years > 0 ? years : 1
        };

      default:
        return { point_expire: EXPIRATION_TYPES.NONE };
    }
  };

  const createExpirationSetting = values => {
    const currentDate = new Date();
    const uniqueCode = generateUniqueCode();

    switch (values.point_expire) {
      case EXPIRATION_TYPES.NONE:
        return {
          code: `NONE_${uniqueCode}`,
          expiration_type: 'NONE',
          is_active: true
        };

      case EXPIRATION_TYPES.GRADUAL:
        return {
          code: `GRADUAL_${values.expire_duration}M_${uniqueCode}`,
          expiration_type: 'GRADUAL',
          expire_amount: parseInt(values.expire_duration),
          expire_unit: 'MONTH',
          expire_from: values.expire_from,
          is_active: true
        };

      case EXPIRATION_TYPES.FIXED_DATE:
        const [day, month] = values.expire_day_month.split('/');
        const expireYear = currentDate.getFullYear() + parseInt(values.expire_years);
        const expireDate = `${expireYear}-${month}-${day}`;

        return {
          code: `FIXED_${day}${month}${expireYear}_${uniqueCode}`,
          expiration_type: 'FIXED_DATE',
          expire_fixed_date: expireDate,
          is_active: true
        };

      default:
        return {
          code: `DEFAULT_${uniqueCode}`,
          expiration_type: 'NONE',
          is_active: true
        };
    }
  };

  const getExpirationMessage = values => {
    const currentDate = new Date();
    const currentDateThai = formatDateThai(currentDate);

    switch (values.point_expire) {
      case EXPIRATION_TYPES.NONE:        
        return `ได้รับคะแนนเมื่อวันที่ ${currentDateThai} คะแนนจะไม่มีวันหมดอายุ`;

      case EXPIRATION_TYPES.GRADUAL:
        const duration = parseInt(values.expire_duration) || 12;
        if (values.expire_from === EXPIRE_FROM_TYPES.EARNED_DATE) {
          const expireDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + duration, 1);
          const expireDateThai = formatDateThai(expireDate);
          return `ได้รับคะแนนเมื่อวันที่ ${currentDateThai} คะแนนจะหมดอายุวันที่ ${expireDateThai}`;
        } else {
          const earnedYear = currentDate.getFullYear();
          const expireDate = new Date(earnedYear + 1, duration, 1);
          const expireDateThai = formatDateThai(expireDate);
          return `ได้รับคะแนนเมื่อวันที่ ${currentDateThai} คะแนนจะหมดอายุวันที่ ${expireDateThai}`;
        }

      case EXPIRATION_TYPES.FIXED_DATE:
        const years = parseInt(values.expire_years) || 1;
        const dayMonth = values.expire_day_month || '01/01';
        const baseYear = currentDate.getFullYear();
        const expireYear = baseYear + years;
        const [day, month] = dayMonth.split('/');
        const fixedExpireDate = new Date(expireYear, parseInt(month) - 1, parseInt(day));
        const fixedExpireDateThai = formatDateThai(fixedExpireDate);

        return `ได้รับคะแนนเมื่อวันที่ ${currentDateThai} คะแนนจะหมดอายุวันที่ ${fixedExpireDateThai}`;

      default:
        return '';
    }
  };

  const fetchBusinessPointSettings = async () => {
    try {
      const response = await dispatch(getAllBusinessPointSettings(businessSlug));

      if (response && Array.isArray(response)) {
        const spendSetting = response.find(setting => setting.type === 'SPEND');

        if (spendSetting) {
          setExistingSettingId(spendSetting.id);
          const expirationData = mapExpirationToForm(spendSetting.businessPointExpiration);

          setFormdata({
            ...DEFAULT_FORM_DATA,
            usagePoint: spendSetting.point || 0,
            discount: parseFloat(spendSetting.exchange_rate) || 0,
            ...expirationData
          });

          setFormdisabled(true);
        }
      }
    } catch (error) {
      console.error('Error fetching business point settings:', error);
    }
  };

  const saveBusinessPointSetting = async values => {
    try {
      const expirationSetting = createExpirationSetting(values);
      const settingData = {
        exchange_rate: values.discount,
        point: values.usagePoint,
        type: 'SPEND',
        expiration_setting: expirationSetting
      };

      if (existingSettingId) {
        await dispatch(updateBusinessPointSetting(businessSlug, settingData));
      } else {
        const createData = { business_slug: businessSlug, ...settingData };
        await dispatch(createBusinessPointSetting(createData));
      }

      setShowSuccessModal(true);
      setFormdisabled(true);
      await fetchBusinessPointSettings();
    } catch (error) {
      console.error('Error saving business point setting:', error);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่');
    }
  };

  const handleFormSubmit = values => saveBusinessPointSetting(values);

  const handleCancelEdit = () => {
    fetchBusinessPointSettings();
    setFormdisabled(true);
  };

  const handleCloseSuccessModal = () => setShowSuccessModal(false);

  useEffect(() => {
    fetchBusinessPointSettings();
  }, [businessSlug]);

  const renderCampaignSettings = (values, handleChange, errors, touched) => (
    <div className="card card-st-2 mb-4">
      <div className="card-body !px-7 py-4">
        <div className="d-flex align-items-center mb-4">
          <div className="tc-8 fs-20 fw-6 me-auto">ข้อกำหนดและเงื่อนไข</div>
        </div>

        <div className="row align-items-center">
          <div className="col-3">
            <Form.Group className="mb-3 form-st-3 pb-1">
              <Form.Label className="mb-1">รูปแบบแคมเปญ</Form.Label>
              <Form.Control type="text" name="campaignFormat" value={values.campaignFormat} disabled />
            </Form.Group>
          </div>

          <div className="col-2">
            <Form.Group className="mb-3 form-st-3 pb-1">
              <Form.Label className="mb-1">คะแนนที่ใช้</Form.Label>
              <Form.Control
                type="number"
                name="usagePoint"
                placeholder="คะแนนที่ใช้"
                value={values.usagePoint}
                onChange={handleChange}
                isInvalid={!!errors.usagePoint}
              />
              {errors.usagePoint && touched.usagePoint && (
                <Form.Control.Feedback type="invalid">{errors.usagePoint}</Form.Control.Feedback>
              )}
            </Form.Group>
          </div>

          <div className="col-2">
            <Form.Group className="mb-3 form-st-3 pb-1">
              <Form.Label className="mb-1">ส่วนลดที่ได้รับ</Form.Label>
              <Form.Control
                type="number"
                name="discount"
                placeholder="ส่วนลดที่ได้รับ"
                value={values.discount}
                onChange={handleChange}
                isInvalid={!!errors.discount}
              />
              {errors.discount && touched.discount && (
                <Form.Control.Feedback type="invalid">{errors.discount}</Form.Control.Feedback>
              )}
            </Form.Group>
          </div>

          <div className="col-4">
            <Form.Group className="mb-3 form-st-3 pb-1">
              <Form.Label className="mb-1">ผลลัพธ์</Form.Label>
              <Form.Control
                type="text"
                value={`${values.usagePoint} คะแนน ได้รับส่วนลด ${values.discount} บาท`}
                disabled
                style={{ color: '#F95057' }}
              />
            </Form.Group>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGradualExpiration = (values, handleChange, setFieldValue, errors, touched) => (
    <div className="mt-3 ms-4">
      <div className="row align-items-center">
        <div className="col-auto">
          <strong>คะแนนมีอายุ</strong>
        </div>
        <div className="col-auto">
          <Form.Control
            type="number"
            name="expire_duration"
            value={values.expire_duration}
            onChange={handleChange}
            style={{ width: '80px' }}
            min="1"
            disabled={formdisabled}
            isInvalid={!!errors.expire_duration}
          />
          {errors.expire_duration && touched.expire_duration && (
            <Form.Control.Feedback type="invalid">{errors.expire_duration}</Form.Control.Feedback>
          )}
        </div>
        <div className="col-auto">
          <strong>เดือน</strong>
        </div>
      </div>

      <div className="mt-2 space-y-4">
        <Form.Check
          type="radio"
          id="count_from_receive"
          name="expire_from"
          label="นับจาก เดือนที่ได้รับคะแนน"
          checked={values.expire_from === EXPIRE_FROM_TYPES.EARNED_DATE}
          onChange={() => setFieldValue('expire_from', EXPIRE_FROM_TYPES.EARNED_DATE)}
          disabled={formdisabled}
        />
        <Form.Check
          type="radio"
          id="count_from_year"
          name="expire_from"
          label="นับจาก ปีที่ได้รับคะแนน"
          checked={values.expire_from === EXPIRE_FROM_TYPES.EARNED_YEAR}
          onChange={() => setFieldValue('expire_from', EXPIRE_FROM_TYPES.EARNED_YEAR)}
          disabled={formdisabled}
        />
      </div>
    </div>
  );

  const renderFixedDateExpiration = (values, handleChange, setFieldValue, errors, touched) => (
    <div className="mt-3 ms-4">
      <div className="row align-items-center">
        <div className="col-auto">
          <DateRangePicker
            singleDatePicker
            showDropdowns
            initialSettings={{
              singleDatePicker: true,
              showDropdowns: true,
              locale: { format: 'DD/MM' }
            }}
            onApply={(event, picker) => {
              const date = picker.startDate;
              const day = date.format('DD');
              const month = date.format('MM');
              setFieldValue('expire_day_month', `${day}/${month}`);
            }}
            disabled={formdisabled}
          >
            <Form.Control
              type="text"
              value={values.expire_day_month}
              placeholder="เลือกวันที่"
              readOnly
              style={{ width: '150px', cursor: 'pointer' }}
            />
          </DateRangePicker>
          {errors.expire_day_month && touched.expire_day_month && (
            <Form.Control.Feedback type="invalid">{errors.expire_day_month}</Form.Control.Feedback>
          )}
        </div>
        <div className="col-auto">
          <Form.Control
            type="number"
            name="expire_years"
            value={values.expire_years}
            onChange={handleChange}
            disabled={formdisabled}
            style={{ width: '80px' }}
            min="1"
            isInvalid={!!errors.expire_years}
          />
          {errors.expire_years && touched.expire_years && (
            <Form.Control.Feedback type="invalid">{errors.expire_years}</Form.Control.Feedback>
          )}
        </div>
        <div className="col-auto">
          <span>ปี</span>
        </div>
      </div>
    </div>
  );

  const renderExpirationSettings = (values, handleChange, setFieldValue, errors, touched) => (
    <div className="card card-st-2 mb-4">
      <div className="card-body !px-7 py-4">
        <div className="d-flex align-items-center mb-7">
          <div className="tc-8 fs-20 fw-6 me-auto">ตั้งค่าการหมดอายุของคะแนน</div>
        </div>

        <div className="mb-4">
          <div className="mb-3">
            <Form.Check
              type="radio"
              id="no_expire"
              name="point_expire"
              label="ไม่มีวันหมดอายุ"
              checked={values.point_expire === EXPIRATION_TYPES.NONE}
              onChange={() => setFieldValue('point_expire', EXPIRATION_TYPES.NONE)}
              disabled={formdisabled}
            />
          </div>

          <div className="mb-3">
            <Form.Check
              type="radio"
              id="duration_expire"
              name="point_expire"
              label="หมดอายุแบบค่อยเป็นค่อยไป (ขึ้นอยู่กับเดือน/ปีที่ได้รับคะแนน)"
              checked={values.point_expire === EXPIRATION_TYPES.GRADUAL}
              onChange={() => setFieldValue('point_expire', EXPIRATION_TYPES.GRADUAL)}
              disabled={formdisabled}
            />
            {values.point_expire === EXPIRATION_TYPES.GRADUAL &&
              renderGradualExpiration(values, handleChange, setFieldValue, errors, touched)}
          </div>

          <div className="mb-3">
            <Form.Check
              type="radio"
              id="fixed_date_expire"
              name="point_expire"
              label="กำหนดวันหมดอายุ (คะแนนทั้งหมดจะหมดอายุในวันที่ระบุ)"
              checked={values.point_expire === EXPIRATION_TYPES.FIXED_DATE}
              onChange={() => setFieldValue('point_expire', EXPIRATION_TYPES.FIXED_DATE)}
              disabled={formdisabled}
            />
            {values.point_expire === EXPIRATION_TYPES.FIXED_DATE &&
              renderFixedDateExpiration(values, handleChange, setFieldValue, errors, touched)}
          </div>

          <div className="mt-4">
            <div
              className="p-3"
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}
            >
              <strong>ผลลัพธ์</strong>
              <div className="mt-2" style={{ color: '#F95057' }}>
                {getExpirationMessage(values)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActionButtons = () => (
    <div className="card card-st-2 mb-4">
      <div className="card-body !px-7">
        <div className="text-end">
          {formdisabled ? (
            <Button
              type="button"
              className="btn-st-3"
              style={{ width: '100px' }}
              onClick={() => setFormdisabled(false)}
            >
              แก้ไข
            </Button>
          ) : (
            <div className="text-end pt-3 btn-group-control">
              <Button type="button" className="btn-st-5 btn text-decoration-none me-3" onClick={handleCancelEdit}>
                ยกเลิก
              </Button>
              <Button type="submit" className="btn-st-3" style={{ width: '100px' }}>
                บันทึก
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Formik enableReinitialize initialValues={formdata} validationSchema={schema} onSubmit={handleFormSubmit}>
        {({ handleSubmit, handleChange, touched, errors, values, setFieldValue }) => (
          <Form className="px-4" noValidate onSubmit={handleSubmit}>
            <fieldset disabled={formdisabled}>
              {renderCampaignSettings(values, handleChange, errors, touched)}
              {renderExpirationSettings(values, handleChange, setFieldValue, errors, touched)}
            </fieldset>
            {renderActionButtons()}
          </Form>
        )}
      </Formik>

      <ProductSuccessModal show={showSuccessModal} onHide={handleCloseSuccessModal} operationType="include" />
    </div>
  );
}
